import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography, TextField, Button, Alert } from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useTranslation } from "react-i18next";
import axiosPublic from "../api/axiosPublic";

export default function ResetPassword() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [token, setToken] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Supabase appends the token in the URL hash:
        // /reset-password#access_token=TOKEN&type=recovery
        const hash = window.location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const accessToken = params.get("access_token");
        const type = params.get("type");

        if (accessToken && type === "recovery") {
            setToken(accessToken);
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 8) {
            setError(t('auth.password_too_short'));
            return;
        }
        if (newPassword !== confirmPassword) {
            setError(t('auth.passwords_mismatch'));
            return;
        }

        setLoading(true);
        try {
            await axiosPublic.post("/api/app_user/reset-password", { token, newPassword });
            setSuccess(true);
        } catch (err) {
            setError(t('auth.reset_password_failed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f0eb" }}>
            <Paper elevation={3} sx={{ p: 5, maxWidth: 440, width: "100%", borderRadius: 4, textAlign: "center" }}>
                <LockResetIcon sx={{ fontSize: 56, color: "#C4622D", mb: 2 }} />
                <Typography variant="h5" fontWeight={700} mb={1} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {t('auth.reset_password_title')}
                </Typography>

                {!token ? (
                    <>
                        <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                            {t('auth.reset_link_invalid')}
                        </Alert>
                        <Button fullWidth variant="outlined" component="a" href="/forgot-password">
                            {t('auth.send_reset_link')}
                        </Button>
                    </>
                ) : success ? (
                    <>
                        <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
                            {t('auth.reset_password_success')}
                        </Alert>
                        <Button fullWidth variant="contained"
                                sx={{ bgcolor: "#C4622D", "&:hover": { bgcolor: "#9A4A1F" } }}
                                component="a" href="/login">
                            {t('auth.back_to_login')}
                        </Button>
                    </>
                ) : (
                    <Box component="form" onSubmit={handleSubmit}>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                            {t('auth.reset_password_subtitle')}
                        </Typography>
                        {error && <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>{error}</Alert>}
                        <TextField
                            fullWidth
                            label={t('auth.new_password')}
                            type="password"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            required
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label={t('auth.confirm_password')}
                            type="password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                            sx={{ mb: 3 }}
                        />
                        <Button type="submit" fullWidth variant="contained" size="large"
                                disabled={loading}
                                sx={{ mb: 2, bgcolor: "#C4622D", "&:hover": { bgcolor: "#9A4A1F" } }}>
                            {loading ? t('common.saving') : t('auth.set_password_btn')}
                        </Button>
                        <Button fullWidth variant="text" component="a" href="/login">
                            {t('auth.back_to_login')}
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
